import * as React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Download, FileText, Play, ShieldAlert, UserCog } from "lucide-react";
import { Breadcrumb } from "@/components/ui/breadcrumb";
import { Button, buttonVariants } from "@/components/ui/button";
import { Table, TableWrapper } from "@/components/ui/table";
import { Progress } from "@/components/ui/progress";
import { PageHeader } from "@/components/data-display/page-header";
import { StatCard } from "@/components/data-display/stat-card";
import { FilterBar, FilterSearch, FilterSelect } from "@/components/data-display/filter-bar";
import { toast } from "@/components/ui/toast";
import { localOnly } from "@/lib/fixture-notice";
import { cn } from "@/lib/utils";
import { PermissionGate } from "@/components/permission/permission-gate";
import { usePermission } from "@/hooks/use-permission";
import {
  EXCEPTIONS,
  EXCEPTION_STATUS_FILTERS,
  EXCEPTION_TYPE_FILTERS,
  LEDGER_COMPARISON,
  RECON_CURRENT_RUN,
  RECON_LAST_RUN,
  RECON_RUNS,
  RECON_RUN_TREND,
  RECON_SOURCES,
  RECON_STATS,
  type ExceptionStatus,
  type ReconRun,
} from "./oversight-data";
import { EmptyRow, EventTimeline, Panel, ToneBadge, statusToneFor } from "./oversight-ui";

export function ReconciliationOverview() {
  const rerun = () => {
    toast({
      variant: "info",
      title: "Reconciliation run queued",
      description: localOnly("RUN-006 will start after the current run (RUN-005) completes."),
    });
  };

  return (
    <div>
      <PageHeader
        title="Reconciliation"
        subtitle="Continuously match platform ledger entries against custody providers and chain sources."
        actions={
          <>
            <PermissionGate permission="reconciliation:run">
              <Button variant="primary" size="sm" onClick={rerun}>
                <Play aria-hidden className="size-4" /> Run reconciliation
              </Button>
            </PermissionGate>
            <Link to="/reconciliation/exceptions" className={cn(buttonVariants({ variant: "secondary", size: "sm" }))}>
              View Exceptions <ArrowRight aria-hidden className="size-4" />
            </Link>
          </>
        }
      />

      <div className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
        {RECON_STATS.map((s) => (
          <StatCard key={s.label} value={s.value} label={s.label} tone={s.tone} />
        ))}
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        <Panel
          title={`Current Run: ${RECON_CURRENT_RUN.id}`}
          actions={<ToneBadge tone="info">Running</ToneBadge>}
          bodyClassName="px-5 py-4"
        >
          <p className="mb-3 text-xs text-muted">Started {RECON_CURRENT_RUN.started} ·</p>
          <Progress
            value={RECON_CURRENT_RUN.percent}
            tone="info"
            label={`${RECON_CURRENT_RUN.processed} / ${RECON_CURRENT_RUN.total} transactions processed (${RECON_CURRENT_RUN.percent}%)`}
          />
        </Panel>

        <Panel title={`Last Completed Run: ${RECON_LAST_RUN.id}`} bodyClassName="px-5 py-4">
          <dl className="grid grid-cols-2 gap-y-4 text-xs sm:grid-cols-3 xl:grid-cols-5">
            {[
              { k: "Completed", v: RECON_LAST_RUN.completed, span: true },
              { k: "Duration", v: RECON_LAST_RUN.duration },
              { k: "Matched", v: String(RECON_LAST_RUN.matched) },
              { k: "Unmatched", v: String(RECON_LAST_RUN.unmatched) },
              { k: "Breaks", v: String(RECON_LAST_RUN.breaks) },
            ].map((r) => (
              <div key={r.k} className={cn(r.span && "sm:col-span-1")}>
                <dt className="text-muted">{r.k}</dt>
                <dd className="mt-0.5 font-semibold text-ink">{r.v}</dd>
              </div>
            ))}
          </dl>
        </Panel>
      </div>

      <h2 className="mb-3 text-[15px] font-bold text-ink">Source Freshness Matrix</h2>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
        {RECON_SOURCES.map((s) => (
          <div
            key={s.name}
            className="flex items-center gap-3 rounded-lg border border-line bg-surface px-4 py-3"
          >
            <span className="min-w-0 flex-1 text-sm font-semibold text-ink">{s.name}</span>
            <ToneBadge tone={s.statusTone}>{s.statusLabel}</ToneBadge>
            <span className="text-xs text-muted">{s.ago}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ExceptionQueue() {
  const [type, setType] = React.useState("");
  const [status, setStatus] = React.useState("");
  const [query, setQuery] = React.useState("");
  const [selectedId, setSelectedId] = React.useState(EXCEPTIONS[0].id);
  const [overrides, setOverrides] = React.useState<Record<string, ExceptionStatus>>({});
  const canResolve = usePermission("reconciliation:run");
  const detailRef = React.useRef<HTMLDivElement>(null);

  const rows = React.useMemo(
    () =>
      EXCEPTIONS.map((e) => ({ ...e, status: overrides[e.id] ?? e.status })).filter((e) => {
        const q = query.toLowerCase();
        return (
          (!q || `${e.id} ${e.type} ${e.wallet} ${e.walletName} ${e.amount}`.toLowerCase().includes(q)) &&
          (!type || e.type === type) &&
          (!status || e.status === status)
        );
      }),
    [type, status, query, overrides]
  );

  const selected = rows.find((e) => e.id === selectedId) ?? rows[0] ?? EXCEPTIONS.find((e) => e.id === selectedId) ?? null;

  const select = (id: string) => {
    setSelectedId(id);
    requestAnimationFrame(() => detailRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }));
  };

  const resolve = (id: string) => {
    setOverrides((prev) => ({ ...prev, [id]: "Resolved" }));
    toast({ variant: "success", title: `${id} resolved`, description: localOnly("Exception closed and appended to the audit log.") });
  };

  return (
    <div>
      <Breadcrumb items={[{ label: "Reconciliation", href: "/reconciliation" }, { label: "Exception Queue" }]} />
      <PageHeader title="Exception Queue" subtitle="Breaks and mismatches requiring investigation or assignment." />

      <FilterBar className="mb-5">
        <FilterSearch value={query} onChange={setQuery} placeholder="Search exceptions by ID, wallet, amount…" />
        <FilterSelect value={type} onChange={setType} options={EXCEPTION_TYPE_FILTERS} ariaLabel="Filter by type" />
        <FilterSelect value={status} onChange={setStatus} options={EXCEPTION_STATUS_FILTERS} ariaLabel="Filter by status" />
      </FilterBar>

      <TableWrapper>
        <Table className="dense">
          <thead>
            <tr className="border-b border-line bg-bg text-left text-xs font-semibold text-muted">
              <th className="px-4 py-2.5">ID</th>
              <th className="px-4 py-2.5">Run</th>
              <th className="px-4 py-2.5">Type</th>
              <th className="px-4 py-2.5">Wallet</th>
              <th className="px-4 py-2.5">Amount</th>
              <th className="px-4 py-2.5">Status</th>
              <th className="px-4 py-2.5">Assigned</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <EmptyRow colSpan={7}>No exceptions match your filters.</EmptyRow>
            ) : (
              rows.map((e) => (
                <tr
                  key={e.id}
                  onClick={() => select(e.id)}
                  aria-current={selected?.id === e.id}
                  className={cn(
                    "cursor-pointer border-b border-line last:border-b-0",
                    selected?.id === e.id && "bg-primary-soft"
                  )}
                >
                  <td className="px-4 py-3 font-mono text-[13px] font-semibold text-ink">{e.id}</td>
                  <td className="px-4 py-3 font-mono text-[13px] text-text">{e.run}</td>
                  <td className="px-4 py-3 text-[13px] text-text">{e.type}</td>
                  <td className="px-4 py-3 text-[13px] text-text">
                    <span className="font-mono">{e.wallet}</span>
                    <span className="ml-1.5 text-muted">{e.walletName}</span>
                  </td>
                  <td className="px-4 py-3 text-[13px] tabular-nums text-ink">{e.amount}</td>
                  <td className="px-4 py-3">
                    <ToneBadge tone={statusToneFor(e.status)}>{e.status}</ToneBadge>
                  </td>
                  <td className="px-4 py-3 text-[13px] text-muted">{e.assigned}</td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </TableWrapper>

      {selected && (
        <div ref={detailRef} className="mt-5 scroll-mt-5">
          <Panel
            title={`${selected.id} · ${selected.type}`}
            actions={<ToneBadge tone={statusToneFor(selected.status)}>{selected.status}</ToneBadge>}
            bodyClassName="px-5 py-4"
          >
            <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-xs sm:grid-cols-3">
              <div>
                <dt className="text-muted">Wallet</dt>
                <dd className="mt-0.5 font-semibold text-ink">
                  {selected.wallet} ({selected.walletName})
                </dd>
              </div>
              <div>
                <dt className="text-muted">Asset</dt>
                <dd className="mt-0.5 font-semibold text-ink">{selected.asset}</dd>
              </div>
              <div>
                <dt className="text-muted">Alloy Balance</dt>
                <dd className="mt-0.5 font-semibold text-ink">{selected.alloyBalance}</dd>
              </div>
              <div>
                <dt className="text-muted">Provider Balance</dt>
                <dd className="mt-0.5 font-semibold text-ink">{selected.providerBalance}</dd>
              </div>
              <div>
                <dt className="text-muted">Difference</dt>
                <dd className={cn("mt-0.5 font-semibold", selected.difference.startsWith("+") ? "text-danger" : "text-ink")}>
                  {selected.difference}
                </dd>
              </div>
              <div>
                <dt className="text-muted">Detected</dt>
                <dd className="mt-0.5 font-semibold text-ink">{selected.detected}</dd>
              </div>
            </dl>

            <div className="mt-5 border-t border-line pt-4">
              <div className="mb-3 flex items-center gap-2">
                <ShieldAlert aria-hidden className="size-4 text-warning" />
                <span className="text-[13px] font-bold text-ink">Investigation Timeline</span>
              </div>
              <EventTimeline
                events={selected.investigation.map((i) => ({ title: "Investigation update", meta: i.desc, ts: i.ts, tone: "info" as const }))}
              />
            </div>

            <div className="mt-5 flex flex-wrap items-center gap-2 border-t border-line pt-4">
              {canResolve && (
                <Button
                  size="sm"
                  variant="success"
                  disabled={selected.status === "Resolved" || selected.status === "Auto-resolved"}
                  onClick={() => resolve(selected.id)}
                >
                  Resolve Exception
                </Button>
              )}
              <Button
                size="sm"
                variant="secondary"
                onClick={() =>
                  toast({
                    variant: "info",
                    title: `Reassign ${selected.id}`,
                    description: "Ownership reassignment opens once the assignment module is built.",
                  })
                }
              >
                <UserCog aria-hidden className="size-4" /> Reassign
              </Button>
              {selected.evidenceId && (
                <Link
                  to={`/evidence/${selected.evidenceId}`}
                  className={cn(buttonVariants({ variant: "ghost", size: "sm" }))}
                >
                  <FileText aria-hidden className="size-4" /> View Evidence Packet
                </Link>
              )}
            </div>
          </Panel>
        </div>
      )}
    </div>
  );
}

export function LedgerComparison() {
  return (
    <div>
      <Breadcrumb items={[{ label: "Reconciliation", href: "/reconciliation" }, { label: "Ledger Comparison" }]} />
      <PageHeader
        title="Ledger Comparison"
        subtitle="Balances as recorded on the Alloy ledger, custody providers, and bank records for the latest completed run."
      />

      <div className="space-y-5">
        {LEDGER_COMPARISON.map((card) => (
          <Panel key={card.id} title={`${card.id} · ${card.name}`} bodyClassName="p-0">
            <p className="px-5 pt-4 text-xs text-muted">{card.meta}</p>
            <TableWrapper className="rounded-none border-0">
              <Table className="dense">
                <thead>
                  <tr className="border-b border-line bg-bg text-left text-xs font-semibold text-muted">
                    <th className="px-5 py-2.5">{card.headers[0]}</th>
                    {card.headers.slice(1).map((h) => (
                      <th key={h} className="px-5 py-2.5">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {card.rows.map((row) => (
                    <tr key={row.asset} className="border-b border-line last:border-b-0">
                      <td className="px-5 py-3 text-[13px] font-semibold text-ink">{row.asset}</td>
                      {row.cells.map((cell) => (
                        <td
                          key={cell.label}
                          className={cn(
                            "px-5 py-3 text-[13px] tabular-nums",
                            cell.matched ? "text-success" : "bg-danger-soft font-semibold text-danger"
                          )}
                        >
                          {cell.value}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </TableWrapper>
            <p className={cn("px-5 py-3 text-xs", card.noteTone === "danger" ? "text-danger" : "text-success")}>
              {card.note}
              {card.noteLink && (
                <>
                  {" "}
                  <Link to={card.noteLink.href} className="font-medium text-primary underline-offset-2 hover:underline">
                    {card.noteLink.label}
                  </Link>
                </>
              )}
            </p>
          </Panel>
        ))}
      </div>
    </div>
  );
}

function runExport(run: ReconRun) {
  return {
    runId: run.id,
    started: run.started,
    status: run.status,
    matched: run.matched,
    unmatched: run.unmatched,
    breaks: run.breaks,
    duration: run.duration,
  };
}

function exportRuns(format: "csv" | "json" | "pdf") {
  const HEADERS = ["Run ID", "Started", "Status", "Matched", "Unmatched", "Breaks", "Duration"];
  const rows = RECON_RUNS.map((r) => [
    r.id,
    r.started,
    r.status,
    String(r.matched),
    String(r.unmatched),
    String(r.breaks),
    r.duration,
  ]);

  let content = "";
  const filename = `reconciliation-runs.${format}`;
  let mime = "text/csv";

  if (format === "csv") {
    content = [HEADERS.join(","), ...rows.map((r) => r.map((c) => (c.includes(",") ? `"${c}"` : c)).join(","))].join("\n");
  } else if (format === "json") {
    mime = "application/json";
    content = JSON.stringify(RECON_RUNS.map(runExport), null, 2);
  } else {
    mime = "application/pdf";
    content = [
      "Alloy — Reconciliation Run History",
      "Generated for Harbor Trust Bank (Production)",
      "",
      `Exported ${RECON_RUNS.length} runs`,
      "",
      ...RECON_RUNS.map((r) =>
        [r.id, r.started, r.status, `matched=${r.matched}`, `unmatched=${r.unmatched}`, `breaks=${r.breaks}`, r.duration].join(" | ")
      ),
    ].join("\n");
  }

  const url = URL.createObjectURL(new Blob([content], { type: mime }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
  toast({ variant: "success", title: "Export generated", description: `Reconciliation run history exported as ${format.toUpperCase()}.` });
}

export function RunHistory() {
  return (
    <div>
      <Breadcrumb items={[{ label: "Reconciliation", href: "/reconciliation" }, { label: "Run History" }]} />
      <PageHeader
        title="Reconciliation Runs"
        subtitle="History of reconciliation runs across sources, with export options."
        actions={
          <>
            <Button size="sm" variant="secondary" onClick={() => exportRuns("csv")}>
              <Download aria-hidden className="size-4" /> Export CSV
            </Button>
            <Button size="sm" variant="secondary" onClick={() => exportRuns("json")}>
              <Download aria-hidden className="size-4" /> Export JSON
            </Button>
            <Button size="sm" variant="secondary" onClick={() => exportRuns("pdf")}>
              <Download aria-hidden className="size-4" /> Export PDF
            </Button>
          </>
        }
      />

      <Panel title="Reconciliation Runs" bodyClassName="p-0">
        <TableWrapper className="rounded-none border-0">
          <Table className="dense">
            <thead>
              <tr className="border-b border-line bg-bg text-left text-xs font-semibold text-muted">
                <th className="px-5 py-2.5">Run ID</th>
                <th className="px-5 py-2.5">Started</th>
                <th className="px-5 py-2.5">Status</th>
                <th className="px-5 py-2.5">Matched</th>
                <th className="px-5 py-2.5">Unmatched</th>
                <th className="px-5 py-2.5">Breaks</th>
                <th className="px-5 py-2.5">Duration</th>
              </tr>
            </thead>
            <tbody>
              {RECON_RUNS.map((r) => (
                <tr key={r.id} className="border-b border-line last:border-b-0">
                  <td className="px-5 py-3 font-mono text-[13px] font-semibold text-ink">{r.id}</td>
                  <td className="px-5 py-3 text-[13px] text-text">{r.started}</td>
                  <td className="px-5 py-3">
                    <ToneBadge tone={statusToneFor(r.status)}>{r.status}</ToneBadge>
                  </td>
                  <td className={cn("px-5 py-3 text-[13px] tabular-nums", r.status === "Running" ? "text-text" : "font-semibold text-ink")}>
                    {r.matched}
                  </td>
                  <td className="px-5 py-3 text-[13px] tabular-nums text-text">{r.unmatched}</td>
                  <td className={cn("px-5 py-3 text-[13px] tabular-nums", r.breaks > 0 ? "font-semibold text-danger" : "text-text")}>
                    {r.breaks}
                  </td>
                  <td className="px-5 py-3 text-[13px] tabular-nums text-muted">{r.duration}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableWrapper>
      </Panel>

      <Panel title="Run Trend (Last 5)" bodyClassName="px-5 py-4">
        <dl className="grid grid-cols-2 gap-y-4 text-xs sm:grid-cols-4">
          {RECON_RUN_TREND.map((t) => (
            <div key={t.label}>
              <dt className="text-muted">{t.label}</dt>
              <dd className="mt-0.5 font-semibold text-ink">{t.value}</dd>
            </div>
          ))}
        </dl>
      </Panel>
    </div>
  );
}